import { useCallback, MouseEvent, FocusEvent } from 'react';

export type TooltipPosition = 'right' | 'top' | 'bottom' | 'bottom-left';

const TOOLTIP_OFFSET = {
  activator: 16,
  default: 8,
} as const;

const TOOLTIP_TOP_BY_POSITION: Record<TooltipPosition, (rect: DOMRect) => number> = {
  right: (rect) => rect.top + rect.height / 2,
  bottom: (rect) => rect.bottom - 70,
  'bottom-left': (rect) => rect.bottom - 63,
  top: (rect) => rect.top,
};

const TOOLTIP_LEFT_BY_POSITION: Record<TooltipPosition, (rect: DOMRect, offset: number) => number> = {
  right: (rect, offset) => rect.right + offset,
  bottom: (rect) => rect.left + 7,
  'bottom-left': (rect) => rect.right,
  top: (rect) => rect.left,
};

const getTooltipAnchor = (wrapper: HTMLElement) => {
  return wrapper.querySelector<HTMLElement>('.search-selector__text-truncate') ?? wrapper;
};

const getTooltipOffset = (wrapper: HTMLElement) => {
  return wrapper.classList.contains('search-selector__activator')
    ? TOOLTIP_OFFSET.activator
    : TOOLTIP_OFFSET.default;
};

const getTooltipTop = (position: TooltipPosition, rect: DOMRect, customTop?: number) => {
  if (customTop !== undefined) {
    return customTop;
  }

  return TOOLTIP_TOP_BY_POSITION[position](rect);
};

/**
 * Hook to manage tooltip positioning using position: fixed.
 * Prevents tooltips from being clipped by containers with overflow: hidden or sidebar boundaries.
 * 
 * @param selector CSS selector for the tooltip inside the parent element.
 * @param position Tooltip position relative to the parent element ('top' or 'right').
 */
export const useFixedTooltip = (selector: string, position: TooltipPosition = 'top', customTop?: number) => {
  const handleTooltipPosition = useCallback((e: MouseEvent<HTMLElement> | FocusEvent<HTMLElement>) => {
    const wrapper = e.currentTarget;
    const tooltip = wrapper.querySelector<HTMLElement>(selector);

    if (!tooltip) {
      return;
    }

    const targetElement = getTooltipAnchor(wrapper);
    const rect = targetElement.getBoundingClientRect();
    const offset = getTooltipOffset(wrapper);
    const top = getTooltipTop(position, rect, customTop);
    const left = TOOLTIP_LEFT_BY_POSITION[position](rect, offset);

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
  }, [selector, position, customTop]);

  return handleTooltipPosition;
};
